"use client";

import React, { useEffect, useState } from "react";
import Image from "next/image";
import { getLenis } from "@/components/SmoothScrollProvider";
import { PROJECT_INFO } from "@/types/projectConsts";
import { cn } from "@/utils/cn";

export default function LeftSidebar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    const handleScroll = () => {
      // Toggle color state once page leaves the hero banner viewport height
      setIsScrolled(window.scrollY > window.innerHeight - 100);

      // Hide sidebar if user is at or near the footer
      const threshold =
        document.documentElement.scrollHeight - window.innerHeight - 300;
      setIsVisible(window.scrollY < threshold);
    };
    window.addEventListener("scroll", handleScroll);
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleScrollDown = () => {
    const sections = [
      "#banner-anchor",
      "#gioi-thieu",
      "#vi-tri",
      "#tien-ich",
      "#mat-bang",
      "#gallery",
      "#tin-tuc",
      "#dang-ky-bao-gia",
    ];

    const lenis = getLenis();
    const scrollPosition = window.scrollY;

    // Find the first section that is below the current viewport top
    let targetSection = null;
    for (const selector of sections) {
      const el = document.querySelector(selector);
      if (el) {
        const top = el.getBoundingClientRect().top + window.scrollY;
        if (top > scrollPosition + 120) {
          targetSection = selector;
          break;
        }
      }
    }

    if (targetSection) {
      if (lenis) {
        lenis.scrollTo(targetSection, { offset: -72 });
      } else {
        const el = document.querySelector(targetSection);
        el?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
    } else {
      // Fallback: Scroll to bottom
      if (lenis) {
        lenis.scrollTo(document.documentElement.scrollHeight);
      } else {
        window.scrollTo({
          top: document.documentElement.scrollHeight,
          behavior: "smooth",
        });
      }
    }
  };

  const themeClass = isScrolled
    ? {
        btn: "border-primary/30 text-primary hover:bg-primary hover:text-white",
        text: "text-primary/60",
        arrow: "text-primary",
      }
    : {
        btn: "border-white/30 text-white/80 hover:bg-white hover:text-secondary-dark",
        text: "text-white/60",
        arrow: "text-white",
      };

  const zaloUrl = PROJECT_INFO.zalo
    ? PROJECT_INFO.zalo.startsWith("http")
      ? PROJECT_INFO.zalo
      : `https://zalo.me/${PROJECT_INFO.zalo.replace(/\s+/g, "")}`
    : "";

  const messengerUrl = PROJECT_INFO.messenger || "";

  if (!isVisible) return null;

  return (
    <div className="fixed left-4 md:left-10 bottom-16 md:bottom-24 z-header flex flex-col items-center gap-5 select-none transition-all duration-300">
      {/* Phone Button */}
      {PROJECT_INFO.hotline && (
        <a
          href={`tel:${PROJECT_INFO.hotline.replace(/\s+/g, "")}`}
          className={cn(
            "w-10 h-10 rounded-full border flex items-center justify-center transition shadow-[0_4px_12px_rgba(0,0,0,0.15)] hover:scale-105",
            themeClass.btn,
          )}
          aria-label="Hotline Call"
          title="Gọi Hotline"
        >
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M3 5a2 2 0 012-2h3.28a1 1 0 01.94.725l.548 2.2a1 1 0 01-.321.988l-1.305.98a10.582 10.582 0 004.872 4.872l.98-1.305a1 1 0 01.988-.321l2.2.548a1 1 0 01.725.94V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
            />
          </svg>
        </a>
      )}

      {/* Zalo Button */}
      {zaloUrl && (
        <a
          href={zaloUrl}
          target="_blank"
          rel="noopener noreferrer"
          className={cn(
            "w-10 h-10 rounded-full border flex items-center justify-center transition shadow-[0_4px_12px_rgba(0,0,0,0.15)] hover:scale-105",
            themeClass.btn,
          )}
          aria-label="Zalo Link"
          title="Liên hệ Zalo"
        >
          <svg
            className="w-5 h-5"
            viewBox="0 0 21 21"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M4.52832 7.88086H7.88632L4.52832 12.9494H7.88632"
              stroke="currentColor"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M19.0539 0.5L4.54836 0.5175C0.236362 4.2175 -1.06364 9.2265 2.78336 16.473C3.02736 17.327 2.60386 18.183 1.78336 19.0375C2.96561 19.1985 4.16835 18.9767 5.21536 18.4045C12.2069 21.5395 16.1749 19.4945 20.0374 17.038L20.0549 1.501C20.0551 1.23587 19.9501 0.981486 19.7629 0.793777C19.5756 0.606067 19.3215 0.500398 19.0564 0.5H19.0539Z"
              stroke="currentColor"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M11.1097 11.6426C11.1097 12.3641 10.4997 12.9486 9.74774 12.9486C8.99574 12.9486 8.38574 12.3636 8.38574 11.6426V10.7936C8.38574 10.0726 8.99574 9.48761 9.74774 9.48761C10.4997 9.48761 11.1097 10.0726 11.1097 10.7936M15.6747 9.48861C16.0346 9.48861 16.3798 9.63158 16.6343 9.88607C16.8888 10.1406 17.0317 10.4857 17.0317 10.8456V11.5926C17.0317 11.9524 16.8888 12.2975 16.6345 12.552C16.3801 12.8064 16.0351 12.9495 15.6752 12.9496C15.3153 12.9496 14.9702 12.8066 14.7157 12.5522C14.4612 12.2977 14.3182 11.9525 14.3182 11.5926V10.8456C14.3182 10.4857 14.4612 10.1406 14.7157 9.88607C14.9702 9.63158 15.3153 9.48861 15.6752 9.48861M11.1097 12.9486V9.48811M12.4502 7.72461V12.2961C12.4502 12.6571 12.7427 12.9496 13.1032 12.9496H13.2992"
              stroke="currentColor"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </a>
      )}

      {/* Messenger Button */}
      {messengerUrl && (
        <a
          href={messengerUrl}
          target="_blank"
          rel="noopener noreferrer"
          className={cn(
            "w-10 h-10 rounded-full border flex items-center justify-center transition shadow-[0_4px_12px_rgba(0,0,0,0.15)] hover:scale-105",
            themeClass.btn,
          )}
          aria-label="Messenger Link"
          title="Liên hệ Messenger"
        >
          <svg className="w-5 h-5 fill-current" viewBox="0 0 24 24">
            <path d="M12 2C6.477 2 2 6.145 2 11.258c0 2.91 1.455 5.517 3.73 7.218V22l3.355-1.844c.915.253 1.887.391 2.915.391 5.523 0 10-4.145 10-9.259C22 6.145 17.523 2 12 2zm1.09 12.445l-2.546-2.714-4.97 2.714 5.466-5.8 2.607 2.714 4.91-2.714-5.467 5.8z" />
          </svg>
        </a>
      )}

      {/* Vertical Scroll Indicator */}
      <div
        onClick={handleScrollDown}
        className="flex flex-col items-center gap-2 mt-3 cursor-pointer group"
      >
        <span
          className={cn(
            "[writing-mode:vertical-lr] uppercase tracking-[0.25em] text-[10px] font-black transition-colors duration-300 group-hover:text-primary",
            themeClass.text,
          )}
        >
          CUỘN XUỐNG
        </span>
        <span
          className={cn(
            "text-lg animate-bounce font-black transition-colors duration-300 group-hover:text-primary",
            themeClass.arrow,
          )}
        >
          ↓
        </span>
      </div>
    </div>
  );
}
